import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { AvatarPessoa } from '@/components/avatar-pessoa'
import { Icon } from '@/components/icon'
import { Button, Card, CardContent, Separator, Skeleton } from '@/components/ui'
import { useAtendimento } from '@/hooks/use-atendimento'
import type { TUltimoAtendimento } from '@/types/atendimento'
import { StringUtils } from '@/utils'
import { formatDaysSince } from '@/utils/date.utils'

export const AtendimentoCard = ({
  clienteId,
  documentoLabel,
  documentoValue,
  diaMes,
  nome,
  quantidadeDias,
  telefone,
}: TUltimoAtendimento) => {
  const { t } = useTranslation(['atendimento', 'common'])
  const navigate = useNavigate()
  const { handleIniciarAtendimento, isIniciandoAtendimento, isEmAtendimento } = useAtendimento()

  return (
    <Card className="shadow-cz border border-gray-200 px-4 py-4 md:px-6" data-testid="atendimento-card">
      <CardContent className="flex flex-col gap-3 p-0">
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <span className="font-semibold text-neutral-700">{diaMes}</span>
          <span className="font-medium text-neutral-600">{formatDaysSince(quantidadeDias)}</span>
        </div>
        <Separator className="bg-neutral-100" />
        <div className="py-1">
          <div className="flex flex-wrap justify-between gap-4">
            <div className="flex space-x-4">
              <AvatarPessoa className="mt-0.5 h-9 w-9 bg-sky-800 text-sm" nome={nome} />
              <div className="flex flex-col gap-1 text-base font-normal tracking-wide">
                <h4 className="text-slate-900">{StringUtils.capitalize(nome)}</h4>
                <p className="text-sm text-neutral-600 md:text-base">
                  {t('common:telefone.label_numero', { numero: telefone })}
                </p>
                <p className="text-sm text-gray-600 md:text-base">
                  {documentoLabel ? `${documentoLabel}: ${documentoValue}` : documentoValue}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              loadingText="Iniciando..."
              className="font-nunito h-8 w-full px-2 pr-4 md:w-42.5"
              onClick={() => handleIniciarAtendimento(clienteId, () => navigate('../historico/simulacoes'))}
              disabled={isEmAtendimento}
              isLoading={isIniciandoAtendimento}
            >
              <Icon name="square-play" className="text-sky-600" size={16} />
              {t('iniciar_atendimento')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export const AtendimentoCardSkeleton = () => {
  return (
    <Card className="border border-gray-200 px-6 py-4 shadow-sm" data-testid="atendimento-card-skeleton">
      <CardContent className="flex flex-col gap-3 p-0">
        <div>
          <div className="flex items-center space-x-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-4 w-16" />
          </div>
        </div>
        <Separator className="bg-neutral-100" />
        <div className="py-1">
          <div className="flex justify-between">
            <div className="flex space-x-4">
              <Skeleton className="mt-0.5 h-9 w-9 rounded-full" />
              <div className="flex flex-col gap-1">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-4 w-36" />
              </div>
            </div>
            <Skeleton className="h-8 w-42" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
